(() => {
  const ROOT_SELECTOR = '.collection-page';
  const MIN_LOADING_DURATION = 650;
  const SETTLING_DURATION = 260;
  const BACK_TO_TOP_SCROLL_GUARD_DURATION = 2000;
  const FRAGMENT_SELECTORS = [
    '.collection-filter',
    '.collection-page__heading-row',
    '.collection-page__mobile-toolbar',
    '.collection-page__count',
    '.collection-page__grid',
    '.collection-pagination',
  ];
  let activeRequest = null;
  let activeRequestUrl = null;
  let settlingTimer = null;
  let isRegistered = false;
  let scrollRestoreSuppressedUntil = 0;
  let scrollIntentVersion = 0;

  const wait = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));
  const motionScrollBehavior = () => (
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
  );
  const suppressScrollRestore = () => {
    scrollIntentVersion += 1;
    scrollRestoreSuppressedUntil = Date.now() + BACK_TO_TOP_SCROLL_GUARD_DURATION;
  };
  const isScrollRestoreSuppressed = () => Date.now() < scrollRestoreSuppressedUntil;

  const hasCollectionFilterScope = (element) => (
    Array.isArray(element?._x_dataStack)
      && element._x_dataStack.some((scope) => (
        typeof scope?.submitFilters === 'function'
          && typeof scope?.submitSort === 'function'
      ))
  );

  const ensureAlpineComponent = (element, hasExpectedScope) => {
    if (!window.Alpine || !element || hasExpectedScope(element)) {
      return;
    }

    if (element._x_dataStack || element._x_marker) {
      if (window.Alpine.destroyTree) {
        window.Alpine.destroyTree(element);
      }

      delete element._x_dataStack;
      delete element._x_marker;
    }

    if (window.Alpine.initTree) {
      window.Alpine.initTree(element);
    }
  };

  const cleanParams = (params) => {
    [...params.entries()].forEach(([key, value]) => {
      if (value === '') {
        params.delete(key);
      }
    });

    params.delete('page');
    params.delete('section_id');
    return params;
  };

  const normalizeFilterValue = (value) => String(value || '').trim().toLowerCase();

  const findFilterCheckbox = (root, filterName, filterValue) => {
    if (!root || !filterValue) {
      return null;
    }

    const normalizedFilterName = String(filterName || '').trim();
    const normalizedFilterValue = normalizeFilterValue(filterValue);
    const checkboxes = root.querySelectorAll?.('.collection-filter input[type="checkbox"][name]') || [];

    return Array.from(checkboxes).find((input) => {
      if (normalizedFilterName && input.name !== normalizedFilterName) {
        return false;
      }

      return normalizeFilterValue(input.value) === normalizedFilterValue;
    }) || null;
  };

  const buildFilterUrl = (form, additionalFilter = null) => {
    const url = new URL(form.action || window.location.href, window.location.origin);
    const params = new URLSearchParams(new FormData(form));

    if (additionalFilter?.name && additionalFilter?.value) {
      const existingValues = params.getAll(additionalFilter.name);

      if (!existingValues.includes(additionalFilter.value)) {
        params.append(additionalFilter.name, additionalFilter.value);
      }
    }

    url.search = params.toString();
    cleanParams(url.searchParams);
    return url;
  };

  const buildSortUrl = (form) => {
    const url = new URL(window.location.href);
    const sortParams = new URLSearchParams(new FormData(form));

    sortParams.forEach((value, key) => {
      if (value === '') {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
    });

    cleanParams(url.searchParams);
    return url;
  };

  const resolveFilterForm = (event) => {
    const target = event?.target || event?.currentTarget;

    if (!target) {
      return null;
    }

    if (typeof HTMLFormElement !== 'undefined' && target instanceof HTMLFormElement) {
      return target;
    }

    if (target.nodeName === 'FORM') {
      return target;
    }

    if (typeof target.closest === 'function') {
      return target.closest('form');
    }

    return null;
  };

  const syncFilterInputsFromUrl = (root, url = new URL(window.location.href)) => {
    if (!root) {
      return;
    }

    root.querySelectorAll('.collection-filter input[type="checkbox"][name]').forEach((input) => {
      const selectedValues = url.searchParams.getAll(input.name);

      input.checked = selectedValues.includes(input.value);
    });
  };

  const createSkeletonGrid = (grid) => {
    if (!grid || grid.querySelector('.collection-page__skeleton-grid')) {
      return;
    }

    const existingCards = grid.querySelectorAll('.collection-product-card__card').length;
    const skeletonCount = Math.min(Math.max(existingCards, 6), 12);
    const skeletonGrid = document.createElement('div');
    skeletonGrid.className = 'collection-page__skeleton-grid';
    skeletonGrid.setAttribute('aria-hidden', 'true');

    skeletonGrid.innerHTML = Array.from({ length: skeletonCount }, () => (
      `<article class="collection-product-card__card collection-product-card__card--skeleton">
        <span class="collection-product-card__skeleton-media"></span>
        <span class="collection-product-card__skeleton-line collection-product-card__skeleton-line--title"></span>
        <span class="collection-product-card__skeleton-line collection-product-card__skeleton-line--price"></span>
        <span class="collection-product-card__skeleton-button"></span>
      </article>`
    )).join('');

    grid.appendChild(skeletonGrid);
  };

  const setLoadingState = (root, isLoading) => {
    const grid = root?.querySelector('.collection-page__grid');
    const content = root?.querySelector('.collection-page__content');

    if (!root || !grid) {
      return;
    }

    if (isLoading) {
      if (settlingTimer) {
        window.clearTimeout(settlingTimer);
        settlingTimer = null;
      }

      root.classList.remove('is-settling');

      if (content) {
        const contentHeight = Math.max(Math.ceil(content.getBoundingClientRect().height), 360);
        content.style.minHeight = `${contentHeight}px`;
      }

      const gridHeight = Math.max(Math.ceil(grid.getBoundingClientRect().height), 280);
      grid.style.setProperty('--collection-loading-min-height', `${gridHeight}px`);
      createSkeletonGrid(grid);
      root.classList.add('is-loading');
      root.setAttribute('aria-busy', 'true');
      return;
    }

    root.classList.remove('is-loading');
    root.classList.add('is-settling');
    root.removeAttribute('aria-busy');
    grid.style.removeProperty('--collection-loading-min-height');
    grid.querySelector('.collection-page__skeleton-grid')?.remove();

    settlingTimer = window.setTimeout(() => {
      root.classList.remove('is-settling');
      root.querySelector('.collection-page__content')?.style.removeProperty('min-height');
      settlingTimer = null;
    }, SETTLING_DURATION);
  };

  const replaceFragment = (root, nextRoot, selector) => {
    const currentElement = root.querySelector(selector);
    const nextElement = nextRoot.querySelector(selector);

    if (!currentElement && !nextElement) {
      return;
    }

    if (currentElement && !nextElement) {
      if (window.Alpine?.destroyTree) {
        window.Alpine.destroyTree(currentElement);
      }

      currentElement.remove();
      return;
    }

    if (!currentElement && nextElement) {
      const grid = root.querySelector('.collection-page__grid');
      const content = root.querySelector('.collection-page__content');

      if (selector === '.collection-pagination' && grid) {
        grid.after(nextElement);
      } else {
        content?.appendChild(nextElement);
      }

      if (window.Alpine?.initTree) {
        window.Alpine.initTree(nextElement);
      }

      return;
    }

    if (window.Alpine?.destroyTree) {
      window.Alpine.destroyTree(currentElement);
    }

    currentElement.replaceWith(nextElement);

    if (window.Alpine?.initTree) {
      window.Alpine.initTree(nextElement);
    }
  };

  const replaceCollectionFragments = (root, nextRoot) => {
    FRAGMENT_SELECTORS.forEach((selector) => {
      replaceFragment(root, nextRoot, selector);
    });
  };

  const replaceCollectionSection = async (url, root, shouldPushState = true, options = {}) => {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      window.location.href = url.toString();
      return;
    }

    const requestUrl = new URL(url.toString());
    requestUrl.searchParams.set('section_id', sectionId);
    const requestUrlKey = requestUrl.toString();

    if (activeRequest && activeRequestUrl === requestUrlKey) {
      return;
    }

    if (activeRequest) {
      activeRequest.abort();
    }

    const requestController = new AbortController();
    activeRequest = requestController;
    activeRequestUrl = requestUrlKey;

    const preserveScroll = options.preserveScroll !== false;
    const loadingStartedAt = performance.now();
    const scrollTop = window.scrollY;
    const requestScrollIntentVersion = scrollIntentVersion;
    setLoadingState(root, true);

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: requestController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        throw new Error(`Collection section request failed: ${response.status}`);
      }

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = documentFragment.querySelector(ROOT_SELECTOR);

      if (!nextRoot) {
        throw new Error('Collection section response did not include the collection root.');
      }

      const remainingLoadingTime = Math.max(0, MIN_LOADING_DURATION - (performance.now() - loadingStartedAt));

      if (remainingLoadingTime > 0) {
        await wait(remainingLoadingTime);
      }

      if (requestController.signal.aborted) {
        return;
      }

      if (shouldPushState) {
        window.history.pushState({}, '', url.toString());
      }

      replaceCollectionFragments(root, nextRoot);
      syncFilterInputsFromUrl(root, url);

      const canRestoreScroll = requestScrollIntentVersion === scrollIntentVersion && !isScrollRestoreSuppressed();

      if (preserveScroll && canRestoreScroll && Math.abs(window.scrollY - scrollTop) > 2) {
        window.scrollTo({ top: scrollTop, left: 0, behavior: 'auto' });
      }

      window.dispatchEvent(new CustomEvent('collection:filters-updated'));
    } catch (error) {
      if (error.name !== 'AbortError') {
        window.location.href = url.toString();
      }
    } finally {
      if (activeRequest === requestController) {
        activeRequest = null;
        activeRequestUrl = null;
        setLoadingState(root, false);
      }
    }
  };

  const createCollectionFilters = () => ({
    filtersOpen: false,
    filterScrollY: 0,
    filterScrollLocked: false,
    hasPendingMobileFilters: false,
    popstateHandler: null,
    filterTrigger: null,
    filterDialog: null,
    mobileViewport: window.matchMedia('(max-width: 989px)').matches,

    init() {
      this.popstateHandler = () => {
        if (!this.isDesignPreview()) {
          replaceCollectionSection(new URL(window.location.href), this.$root, false);
        }
      };

      window.addEventListener('popstate', this.popstateHandler);

      if (!this.isDesignPreview()) {
        syncFilterInputsFromUrl(this.$root);
      }
    },

    destroy() {
      if (this.popstateHandler) {
        window.removeEventListener('popstate', this.popstateHandler);
      }
      window.themeModalAccessibility?.deactivate(this.filterDialog);
      this.syncFilterScrollLock(false);
    },

    isDesignPreview() {
      return this.$root?.dataset.designPreviewMode === 'true';
    },

    openFilters(trigger) {
      if (this.filtersOpen || !this.isMobileViewport()) {
        return;
      }

      this.filterTrigger = trigger || document.activeElement;
      this.filterDialog = this.$root?.querySelector('.collection-page__sidebar');
      this.filtersOpen = true;
      window.themeModalAccessibility?.activate(this.filterDialog);
      const backdrop = this.$root?.querySelector('.collection-page__filter-backdrop');
      if (backdrop) {
        backdrop.inert = false;
      }
      this.$nextTick(() => this.$refs.filterClose?.focus({ preventScroll: true }));
    },

    closeFilters(restoreFocus = true) {
      if (!this.filtersOpen) {
        return;
      }

      const trigger = this.filterTrigger;
      this.filtersOpen = false;
      window.themeModalAccessibility?.deactivate(this.filterDialog);
      this.filterDialog = null;
      this.filterTrigger = null;

      if (restoreFocus && trigger?.isConnected) {
        this.$nextTick(() => trigger.focus({ preventScroll: true }));
      }
    },

    trapFilterFocus(event) {
      window.themeModalAccessibility?.trapFocus(event, event?.currentTarget);
    },

    isMobileViewport() {
      return this.mobileViewport;
    },

    shouldDeferFilterSubmit() {
      return this.filtersOpen && this.isMobileViewport();
    },

    syncFilterScrollLock(isOpen) {
      const html = document.documentElement;
      const body = document.body;

      if (isOpen) {
        html.classList.add('is-collection-filter-open');

        if (this.filterScrollLocked || window.matchMedia('(min-width: 990px)').matches) {
          return;
        }

        this.filterScrollY = window.scrollY || window.pageYOffset;
        body.style.position = 'fixed';
        body.style.top = `-${this.filterScrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        this.filterScrollLocked = true;
        return;
      }

      html.classList.remove('is-collection-filter-open');

      if (!this.filterScrollLocked) {
        return;
      }

      body.style.removeProperty('position');
      body.style.removeProperty('top');
      body.style.removeProperty('left');
      body.style.removeProperty('right');
      body.style.removeProperty('width');
      window.scrollTo(0, this.filterScrollY);
      this.filterScrollLocked = false;
    },

    handleViewportResize() {
      this.mobileViewport = window.matchMedia('(max-width: 989px)').matches;
      if (!this.mobileViewport) {
        this.closeFilters(false);
        this.syncFilterScrollLock(false);
      }
    },

    submitFilters(event) {
      const form = resolveFilterForm(event);

      if (this.isDesignPreview() || !form) {
        return;
      }

      replaceCollectionSection(buildFilterUrl(form), this.$root);
    },

    handleFilterChange(event) {
      if (this.isDesignPreview()) {
        return;
      }

      if (this.shouldDeferFilterSubmit()) {
        this.hasPendingMobileFilters = true;
        return;
      }

      this.submitFilters(event);
    },

    applyFilters(event) {
      const form = resolveFilterForm(event) || this.$root?.querySelector?.('.collection-filter');

      if (this.isDesignPreview() || !form) {
        return;
      }

      this.hasPendingMobileFilters = false;
      this.submitFilters({ target: form });
    },

    submitSort(event) {
      const form = resolveFilterForm(event);

      if (this.isDesignPreview() || !form) {
        return;
      }

      replaceCollectionSection(buildSortUrl(form), this.$root);
    },

    onRootClick(event) {
      if (this.isDesignPreview()) {
        return;
      }

      const heroCta = event.target instanceof Element ? event.target.closest('[data-collection-hero-cta="true"]') : null;

      if (heroCta) {
        const filterName = heroCta.dataset.filterName;
        const filterValue = heroCta.dataset.filterValue;
        const form = this.$root?.querySelector('.collection-filter');

        if (!filterValue || !form) {
          return;
        }

        event.preventDefault();

        const checkbox = findFilterCheckbox(this.$root, filterName, filterValue);
        if (checkbox) {
          checkbox.checked = true;
        }

        this.$root?.querySelector('.collection-page__grid')?.scrollIntoView({
          behavior: motionScrollBehavior(),
          block: 'start',
        });

        replaceCollectionSection(
          buildFilterUrl(form, checkbox ? null : { name: filterName, value: filterValue }),
          this.$root,
          true,
          { preserveScroll: false },
        ).then(() => {
          this.$root?.querySelector('.collection-page__grid')?.scrollIntoView({
            behavior: motionScrollBehavior(),
            block: 'start',
          });
        });
        return;
      }

      const link = event.target instanceof Element ? event.target.closest('.collection-pagination a[href]') : null;

      if (!link) {
        return;
      }

      event.preventDefault();
      replaceCollectionSection(new URL(link.href, window.location.origin), this.$root);
    },
  });

  const registerCollectionFilters = () => {
    if (!window.Alpine || isRegistered) {
      return;
    }

    isRegistered = true;
    window.Alpine.data('collectionFilters', createCollectionFilters);

    requestAnimationFrame(() => {
      document.querySelectorAll(ROOT_SELECTOR).forEach((element) => {
        ensureAlpineComponent(element, hasCollectionFilterScope);
      });
    });
  };

  window.JustFishyCollectionFilters = {
    buildFilterUrl,
    buildSortUrl,
    cleanParams,
    findFilterCheckbox,
    normalizeFilterValue,
    resolveFilterForm,
    syncFilterInputsFromUrl,
    createCollectionFilters,
  };
  window.collectionFilters = createCollectionFilters;

  window.addEventListener('site:back-to-top', suppressScrollRestore);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildFilterUrl,
      buildSortUrl,
      cleanParams,
      createCollectionFilters,
      findFilterCheckbox,
      normalizeFilterValue,
      resolveFilterForm,
      syncFilterInputsFromUrl,
    };
  }

  document.addEventListener('alpine:init', registerCollectionFilters);
  registerCollectionFilters();
})();
